import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { InkPad } from '../../features/ink/InkPad';
import { MathQuestion } from '../../components/math/MathQuestion';
import { ActivityTimer } from '../../lib/learning/activityTimer';
import { finalizeAttempt, pilotSummary, startPilot } from '../../features/pilotMath/store';
import { usePilotState } from '../../features/pilotMath/usePilotState';
import { formatDuration } from '../../features/pilotMath/format';
import type { InkDocumentV1 } from '../../types/ink';
import { env } from '../../lib/env';
import { CloudTodayMathPage } from './CloudTodayMathPage';

function reasonLabel(reason: string) {
  if (reason === 'spaced_review') return 'Review';
  if (reason === 'mastery_check') return 'Cek penguasaan';
  if (reason === 'remediation') return 'Penguatan';
  return 'Latihan saat ini';
}

function LocalTodayMathPage() {
  const [state, setState] = usePilotState();
  const question = state.questions[state.currentIndex];
  const [answer, setAnswer] = useState('');
  const [firstResponse, setFirstResponse] = useState<number | null>(null);
  const [correctionCount, setCorrectionCount] = useState(0);
  const [hintCount, setHintCount] = useState(0);
  const [feedback, setFeedback] = useState<'idle' | 'incorrect' | 'correct'>('idle');
  const [ink, setInk] = useState<InkDocumentV1>({ version: 1, width: 700, height: 220, strokes: [] });
  const timer = useRef<ActivityTimer | null>(null);
  const questionStartedAt = useRef<string>(new Date().toISOString());

  useEffect(() => {
    if (state.status === 'not_started') {
      setState(startPilot(state));
    }
  }, [state, setState]);

  useEffect(() => {
    timer.current = new ActivityTimer();
    questionStartedAt.current = new Date().toISOString();
    setAnswer('');
    setFirstResponse(null);
    setCorrectionCount(0);
    setHintCount(0);
    setFeedback('idle');
    setInk({ version: 1, width: 700, height: 220, strokes: [] });

    let idleTimer = window.setTimeout(() => timer.current?.pause(), 60_000);
    const markActive = () => {
      timer.current?.resume();
      window.clearTimeout(idleTimer);
      idleTimer = window.setTimeout(() => timer.current?.pause(), 60_000);
    };
    const onVisibility = () => {
      if (document.hidden) timer.current?.pause(); else markActive();
    };
    document.addEventListener('visibilitychange', onVisibility);
    document.addEventListener('pointerdown', markActive, { passive: true });
    document.addEventListener('keydown', markActive);
    return () => {
      window.clearTimeout(idleTimer);
      document.removeEventListener('visibilitychange', onVisibility);
      document.removeEventListener('pointerdown', markActive);
      document.removeEventListener('keydown', markActive);
    };
  }, [question?.id]);

  const summary = useMemo(() => pilotSummary(state), [state]);
  const onInkChange = useCallback((document: InkDocumentV1) => setInk(document), []);

  if (state.status === 'completed' || !question) {
    return (
      <div className="stack pilot-finish">
        <section className="card hero-card">
          <div className="eyebrow">Belajar hari ini selesai</div>
          <h1>Kerja bagus, {state.student.displayName}! 🎉</h1>
          <p className="muted">Jawaban pertama, koreksi, dan waktu aktif sudah masuk ke history pilot.</p>
        </section>
        <section className="grid grid--3">
          <div className="metric"><span>First-attempt</span><strong>{summary.firstAttemptAccuracy}%</strong></div>
          <div className="metric"><span>Waktu aktif</span><strong>{formatDuration(summary.totalActiveMs)}</strong></div>
          <div className="metric"><span>Koreksi</span><strong>{summary.corrections}</strong></div>
        </section>
        <section className="card">
          <h2>Apa yang terjadi berikutnya?</h2>
          <p>Engine pilot memperbarui evidence dan mastery per skill. Tutor dapat membuka monitor siswa untuk melihat riwayat yang sama.</p>
          <div className="button-row">
            <Link className="btn btn--primary" to="/student/progress">Lihat progress</Link>
            <Link className="btn" to="/teacher/students">Lihat sebagai tutor</Link>
          </div>
        </section>
      </div>
    );
  }

  const numericAnswer = Number(answer);
  const canSubmit = answer.trim() !== '' && Number.isFinite(numericAnswer);

  function submitAnswer() {
    if (!canSubmit || !question) return;
    const value = Number(answer);
    const initial = firstResponse ?? value;
    if (firstResponse === null) setFirstResponse(value);

    if (value !== question.answer) {
      setFeedback('incorrect');
      setCorrectionCount((count) => count + 1);
      setHintCount((count) => count + 1);
      setAnswer('');
      return;
    }

    setFeedback('correct');
    const duration = timer.current?.snapshot() ?? { elapsedDurationMs: 0, activeDurationMs: 0, pausedDurationMs: 0 };
    const completedAt = new Date().toISOString();
    const next = finalizeAttempt(state, {
      id: `ATT-${question.id}-${Date.now()}`,
      questionId: question.id,
      skillCode: question.skillCode,
      skillLabel: question.skillLabel,
      prompt: question.prompt,
      correctAnswer: question.answer,
      firstResponse: initial,
      finalResponse: value,
      firstAttemptCorrect: initial === question.answer,
      finalCorrect: true,
      correctionCount,
      hintCount,
      startedAt: questionStartedAt.current,
      completedAt,
      duration,
      ...(ink.strokes.length > 0 ? { ink } : {}),
    });
    window.setTimeout(() => setState(next), 320);
  }

  return (
    <div className="stack today-math">
      <header className="learning-header">
        <div>
          <div className="eyebrow">Matematika · Belajar Hari Ini</div>
          <h1>{question.skillLabel}</h1>
          <p className="muted">{reasonLabel(question.reasonCode)} · {question.difficultyBand}</p>
        </div>
        <div className="learning-progress" aria-label={`Soal ${state.currentIndex + 1} dari ${state.questions.length}`}>
          <strong>{state.currentIndex + 1}/{state.questions.length}</strong>
          <div className="progress-track"><span style={{ width: `${((state.currentIndex) / state.questions.length) * 100}%` }} /></div>
        </div>
      </header>

      <section className="card question-card">
        <div className="question-meta">
          <span className="pill">{reasonLabel(question.reasonCode)}</span>
          <span className="muted">{question.skillCode}</span>
        </div>
        <MathQuestion question={question} />

        <InkPad onChange={onInkChange} />

        <div className="answer-panel">
          <label htmlFor="structured-answer"><strong>Jawaban untuk diperiksa</strong></label>
          <div className="answer-row">
            <input
              id="structured-answer"
              className="answer-input"
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="off"
              value={answer}
              onChange={(event) => setAnswer(event.target.value.replace(/[^0-9-]/g, ''))}
              onKeyDown={(event) => { if (event.key === 'Enter') submitAnswer(); }}
              aria-describedby="answer-help"
            />
            <button className="btn btn--primary btn--large" type="button" disabled={!canSubmit} onClick={submitAnswer}>Periksa</button>
          </div>
          <p id="answer-help" className="muted">Pada pilot ini, tulisan stylus disediakan sebagai workspace. Penilaian otomatis memakai angka pada kotak jawaban.</p>
        </div>

        {feedback === 'incorrect' && (
          <div className="feedback feedback--error" role="status">
            <strong>Belum tepat. Coba perbaiki.</strong>
            <p>{hintCount <= 1 ? 'Perhatikan kembali nilai tempat satuan dan puluhan.' : 'Jika satuan tidak cukup atau berjumlah 10+, gunakan pertukaran 1 puluhan ↔ 10 satuan.'}</p>
          </div>
        )}
      </section>

      <section className="learning-footer muted">
        <span>Soal yang sudah selesai: {summary.completed}</span>
        <span>First-attempt: {summary.firstAttemptAccuracy}%</span>
        <span>Active time: {formatDuration(summary.totalActiveMs)}</span>
      </section>
    </div>
  );
}


export function TodayMathPage() {
  if (env.supabaseUrl && env.supabasePublishableKey) return <CloudTodayMathPage />;
  return <LocalTodayMathPage />;
}
